import {
  AcceptInvitationSchema,
  DeclineInvitationSchema,
  DeleteNotificationSchema,
  MarkNotificationAsReadSchema,
  acceptInvitationSchema,
  declineInvitationSchema,
  deleteNotificationSchema,
  markNotificationAsReadSchema,
} from "@/features/notifications/schemas";
import { AppNotification, InvitationData, NotificationActor, NotificationsPage } from "@/features/notifications/types";
import { GetMyNotificationsActorDto, GetMyNotificationsResponse } from "@/features/notifications/types/responses";
import { badRequest } from "@/shared/lib/utils/errors";
import http from "@/shared/lib/utils/http";
import { toErrorDetails } from "@/shared/lib/utils/zod";

function toActor(dto: GetMyNotificationsActorDto): NotificationActor {
  return {
    id: dto.id,
    firstName: dto.first_name,
    lastName: dto.last_name,
    emailAddress: dto.email_address,
  };
}

export async function deleteNotification(values: DeleteNotificationSchema): Promise<undefined> {
  const validationResult = deleteNotificationSchema.safeParse(values);

  if (!validationResult.success) {
    throw badRequest("Delete Notification", toErrorDetails(validationResult.error));
  }

  await http.delete({
    url: `/users/me/notifications/${values.id}`,
    contentType: "none",
  });
}

export async function acceptInvitation(values: AcceptInvitationSchema): Promise<undefined> {
  const validationResult = acceptInvitationSchema.safeParse(values);

  if (!validationResult.success) {
    throw badRequest("Accept Invitation", toErrorDetails(validationResult.error));
  }

  await http.post<undefined>({
    url: `/users/me/notifications/${values.id}/accept`,
    contentType: "none",
  });
}

export async function declineInvitation(values: DeclineInvitationSchema): Promise<undefined> {
  const validationResult = declineInvitationSchema.safeParse(values);

  if (!validationResult.success) {
    throw badRequest("Decline Invitation", toErrorDetails(validationResult.error));
  }

  await http.post<undefined>({
    url: `/users/me/notifications/${values.id}/decline`,
    contentType: "none",
  });
}

export async function markNotificationAsRead(values: MarkNotificationAsReadSchema): Promise<undefined> {
  const validationResult = markNotificationAsReadSchema.safeParse(values);

  if (!validationResult.success) {
    throw badRequest("Mark Notification As Read", toErrorDetails(validationResult.error));
  }

  await http.put<undefined>({
    url: `/users/me/notifications/${values.id}`,
    contentType: "none",
  });
}

export async function getMyNotifications(startingAfter?: number | null): Promise<NotificationsPage> {
  const params = new URLSearchParams();

  if (startingAfter) {
    params.set("starting_after", String(startingAfter));
  }

  const response = await http.get<GetMyNotificationsResponse>({
    url: `/users/me/notifications?${params.toString()}`,
    contentType: "none",
  });

  const notifications = response.notifications.map(
    (dto): AppNotification => ({
      id: dto.id,
      type: dto.type as AppNotification["type"],
      status: dto.status as AppNotification["status"],
      createdAt: new Date(dto.created_at),
      invitationData: dto.invitation_data
        ? {
            project: { id: dto.invitation_data.project.id, name: dto.invitation_data.project.name },
            role: { name: dto.invitation_data.role.name },
            invitationStatus: dto.invitation_data.invitation_status as InvitationData["invitationStatus"],
            sentBy: toActor(dto.invitation_data.actor),
          }
        : undefined,
      projectRemovalData: dto.removal_data
        ? {
            project: { id: dto.removal_data.project.id, name: dto.removal_data.project.name },
            removedBy: toActor(dto.removal_data.actor),
          }
        : undefined,
      projectRoleUpdateData: dto.member_role_update_data
        ? {
            project: { id: dto.member_role_update_data.project.id, name: dto.member_role_update_data.project.name },
            role: { name: dto.member_role_update_data.role.name },
            updatedBy: toActor(dto.member_role_update_data.actor),
          }
        : undefined,
      ticketAssignmentData: dto.ticket_assignment_data
        ? {
            project: { id: dto.ticket_assignment_data.project.id, name: dto.ticket_assignment_data.project.name },
            ticket: dto.ticket_assignment_data.ticket
              ? {
                  id: dto.ticket_assignment_data.ticket.id,
                  key: dto.ticket_assignment_data.ticket.key,
                  title: dto.ticket_assignment_data.ticket.title,
                }
              : null,
            assignedBy: toActor(dto.ticket_assignment_data.actor),
          }
        : undefined,
      ticketUpdateData: dto.ticket_update_data
        ? {
            project: { id: dto.ticket_update_data.project.id, name: dto.ticket_update_data.project.name },
            ticket: dto.ticket_update_data.ticket
              ? {
                  id: dto.ticket_update_data.ticket.id,
                  key: dto.ticket_update_data.ticket.key,
                  title: dto.ticket_update_data.ticket.title,
                }
              : null,
            updatedBy: toActor(dto.ticket_update_data.actor),
          }
        : undefined,
      commentReplyData: dto.comment_reply_data
        ? {
            project: { id: dto.comment_reply_data.project.id, name: dto.comment_reply_data.project.name },
            ticket: dto.comment_reply_data.ticket
              ? {
                  id: dto.comment_reply_data.ticket.id,
                  key: dto.comment_reply_data.ticket.key,
                  title: dto.comment_reply_data.ticket.title,
                }
              : null,
            comment: dto.comment_reply_data.comment
              ? {
                  id: dto.comment_reply_data.comment.id,
                  body: dto.comment_reply_data.comment.body,
                }
              : null,
            repliedBy: toActor(dto.comment_reply_data.actor),
          }
        : undefined,
    })
  );

  return {
    notifications,
    hasMore: response.has_more,
    nextStartingAfter: response.next_starting_after ?? null,
  };
}
